import { AddCircle, Info, InfoSharp, SearchSharp } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { ColumnFields } from '../@types';
import IconRenderer from '../components/atoms/IconRenderer';
import Switch from '../components/atoms/Switch';
import TableIconContainer from '../components/atoms/TableIconContainer';
import { ResponsiveTooltip } from '../components/atoms/ResponsiveTooltip';
import { AdditionalDataGridColumnProps } from '../components/molecules/CustomizeTable';
import EditTableItem from '../components/organisms/EditTableItem';
import { createColumn } from '../utils';
import AgentsReportsCommissions from '../components/atoms/AgentsReportsCommissions';
import { CommissionObj, CommissionPicks } from '../@types/api';
import dayjs from 'dayjs';
import type { FormTypes } from '../components/modals/FormModal';

type HasSportIcon = {
  sport: {
    icon: string;
  };
};

export type HasIsActive = {
  isActive: boolean;
};

type HasId = {
  id: string;
};

type EditColumnOptions = {
  deleteData?: (id: string) => void;
  addOutcome?: (marketId: string, sportId: string) => void;
  columnName?: string;
  icon?: React.ReactNode;
  minWidth?: number;
  formType?: FormTypes;
  formProps?: Record<string, unknown>;
};

type ToggleColumnOptions<T> = {
  updateData: (id: string, data: Partial<T>) => void;
  alwaysVisible?: boolean;
  modalEntity?: string;
  disabled?: boolean;
};
export const getConfirmMessage = (action: string, entity?: string, item?: string) => {
  return `Are you sure you want to ${action} Cash Out for ${entity} ${item}?`;
};
export const getCurrentAction = (checked: boolean) => {
  return checked ? 'enable' : 'disable';
};
export const createToggleColumn = <T,>(options: GridColDef & ToggleColumnOptions<T>): GridColDef => {
  const { field, headerName = 'Status', updateData, alwaysVisible, modalEntity, sortable = true, disabled } = options;

  return createColumn(field, headerName, {
    alwaysVisible,
    sortable,
    renderCell: (params) => (
      <Switch
        value={params.value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          updateData(params.row.id, { [field]: !params.value } as Partial<T>);
        }}
        name={field}
        handleConfirmationMessage={
          field !== 'isActive'
            ? () => getConfirmMessage(getCurrentAction(!params.value), modalEntity, params.row.name)
            : undefined
        }
        disabled={disabled}
      />
    ),
  });
};

export const createSportIconColumn = <T extends HasSportIcon>(): GridColDef => {
  return createColumn('sportIcon', 'Icon', {
    sortable: false,
    valueGetter: (value, row, column, apiRef) => row.sport.icon,
    renderCell: (params) => {
      return (
        <TableIconContainer>
          <IconRenderer name={params.value} />
        </TableIconContainer>
      );
    },
  });
};

export const createIsActiveColumn = <T,>(
  options: Omit<GridColDef, 'field' | 'headerName'> & ToggleColumnOptions<T>
): GridColDef => {
  return createToggleColumn<T>({
    field: 'isActive',
    headerName: 'Status',
    ...options,
  });
};

export const createCashOutColumn = <T,>(
  options: Omit<GridColDef, 'field' | 'headerName'> & ToggleColumnOptions<T>
): GridColDef => {
  return createToggleColumn<T>({
    field: 'eligibleForCashout',
    headerName: 'Cash Out',
    sortable: false,
    ...options,
  });
};

export const createEditColumn = <T extends HasId>(
  options: EditColumnOptions & { extraActions?: (params: GridRenderCellParams<T>) => React.ReactNode } = {}
): GridColDef => {
  const {
    deleteData,
    addOutcome,
    columnName = 'Edit',
    icon,
    minWidth = 100,
    extraActions,
    formType,
    formProps,
  } = options;
  return createColumn('edit', columnName, {
    sortable: false,
    disableExport: true,
    minWidth,
    renderCell: (params) => (
      <>
        {extraActions && <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{extraActions(params)}</Box>}
        {addOutcome && (
          <IconButton onClick={() => addOutcome(params.row.id, params.row.sport.id)} color='primary'>
            <Tooltip title='Add outcome'>
              <AddCircle />
            </Tooltip>
          </IconButton>
        )}
        <EditTableItem<T>
          handleDelete={deleteData ? () => deleteData(params.row.id) : undefined}
          item={params.row}
          icon={icon}
          formType={formType}
          formProps={formProps}
        />
      </>
    ),
  });
};

export const createDescriptionColumn = () =>
  createColumn('description', 'Description', {
    sortable: false,
    renderCell: (params) =>
      params.value && (
        <TableIconContainer>
          <ResponsiveTooltip content={params.value}>
            <Info color='primary' />
          </ResponsiveTooltip>
        </TableIconContainer>
      ),
  });

const numberColumnFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const decimalValuePattern = /^([+-]?)(\d*)(?:\.(\d*))?$/;

const getTruncatedDecimalString = (value: string) => {
  const match = value.match(decimalValuePattern);

  if (!match || (!match[2] && match[3] === undefined)) return null;

  const [, sign, integerPart, decimalPart = ''] = match;
  return `${sign}${integerPart || '0'}.${decimalPart.slice(0, 2).padEnd(2, '0')}`;
};

const formatNumberColumnValue = (value: unknown) => {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return value;
  if (typeof value !== 'number' && typeof value !== 'string') return value;

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;

  const valueString = typeof value === 'string' ? value.trim() : numericValue.toString();
  const truncatedDecimalString = getTruncatedDecimalString(valueString);

  if (truncatedDecimalString) {
    return numberColumnFormatter.format(Number(truncatedDecimalString));
  }

  const truncatedValue = Math.trunc(numericValue * 100) / 100;
  return numberColumnFormatter.format(truncatedValue);
};

export const createNumberColumn = (
  field: ColumnFields | string,
  headerName: string,
  specificProps?: AdditionalDataGridColumnProps & Omit<GridColDef, 'field' | 'headerName'>
) =>
  createColumn(field, headerName, {
    ...specificProps,
    type: 'number',
    valueGetter: (value, row, column, apiRef) => {
      const resolvedValue = specificProps?.valueGetter ? specificProps.valueGetter(value, row, column, apiRef) : value;

      if (specificProps?.hideZeros && !resolvedValue) return null;
      return resolvedValue;
    },
    valueFormatter: (value, row, column, apiRef) => {
      if (specificProps?.hideZeros && !value) return null;
      if (specificProps?.skipFormatting?.(value, row, column, apiRef)) {
        return value;
      }
      return formatNumberColumnValue(value);
    },
  });

const formatPercentageValue = (value: unknown) => (typeof value === 'number' ? `${value.toFixed(2)}%` : value);

export const createPercentageColumn = (
  field: ColumnFields | string,
  headerName: string,
  specificProps?: AdditionalDataGridColumnProps & Omit<GridColDef, 'field' | 'headerName'>
) => {
  return createColumn(field, headerName, {
    valueFormatter: formatPercentageValue,
    ...specificProps,
  });
};

export const createCommissionColumn = <T extends { commission?: CommissionObj }>(
  onCellClick?: (params: GridRenderCellParams<T>) => void
): GridColDef => {
  const commissionIconStyle = { color: 'primary.main', cursor: 'pointer' };

  return createColumn('commission', 'Commission', {
    sortable: false,
    valueGetter: (value, row, column, apiRef) => {
      if (!row.commission) return '';
      if (row.commission?.type === 'PER_PICK') {
        const info = row.commission.info;
        if (!info) return row.commission.type;
        return Object.entries(info as CommissionPicks<number>)
          .map(([picks, commission]) => `${picks} (${commission / 100}%)`)
          .join(', ');
      }
      return row.commission?.type || '';
    },
    renderCell: (params: GridRenderCellParams<T>) => {
      if (!params.row.commission) return null;
      if (params.row.commission?.info) {
        return (
          <Box>
            <SearchSharp sx={commissionIconStyle} onClick={() => onCellClick?.(params)} />
            <Tooltip title={<AgentsReportsCommissions commission={params.row.commission.info} />} placement='right-end'>
              <InfoSharp sx={commissionIconStyle} />
            </Tooltip>
          </Box>
        );
      }
      return params.row.commission?.type || '';
    },
  });
};

export const createDateColumn = (field: ColumnFields | string, headerName: string) => {
  return createColumn(field, headerName, {
    valueGetter: (value, row, column, apiRef) => {
      return value && dayjs(value).format('DD-MM-YYYY HH:mm');
    },
  });
};
